import React, { useState } from 'react'
import toast from 'react-hot-toast'
import roomsService from '../services/roomsService'
import { useTranslation } from '../hooks/use-i18n'
import type { User } from '../types/room'

interface SetStartingPointsModalProps {
  roomId: string
  player: User
  onClose: () => void
  onSuccess: () => void
  /** When true, simulate the save (no API call) — used by the public demo. */
  demoMode?: boolean
  /** Demo only: receives the signed amount and note so the caller can update mock state. */
  onDemoSubmit?: (signedAmount: number, note: string) => void
}

const SetStartingPointsModal: React.FC<SetStartingPointsModalProps> = ({
  roomId,
  player,
  onClose,
  onSuccess,
  demoMode = false,
  onDemoSubmit,
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [mode, setMode] = useState<'add' | 'remove'>('add')
  const [amount, setAmount] = useState<string>('')
  const [note, setNote] = useState<string>('')

  const playerId = player._id

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const magnitude = Math.max(0, Math.min(9999, Math.round(Number(amount) || 0)))
    if (magnitude === 0) {
      toast.error(t('roomPage.startingPointsAmountRequired'))
      return
    }
    const signed = mode === 'remove' ? -magnitude : magnitude
    if (demoMode) {
      onDemoSubmit?.(signed, note.trim())
      const label = signed >= 0 ? `+${signed}` : `${signed}`
      toast.success(`${t('roomPage.startingPointsSaved')} (${label})`)
      onSuccess()
      return
    }
    try {
      setIsLoading(true)
      await roomsService.addPlayerStartingPoints(roomId, playerId, {
        amount: signed,
        note: note.trim() || undefined,
      })
      toast.success(t('roomPage.startingPointsSaved'))
      onSuccess()
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('roomPage.startingPointsSaveFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md mx-4 glass-strong rounded-2xl p-5 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-display font-bold text-foreground">
            {t('roomPage.adjustStartingPoints')}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all"
            aria-label={t('common.close')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-5">
          {t('roomPage.adjustStartingPointsFor', { name: player.name })}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Add / Remove toggle */}
          <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-muted/10 border border-muted/20">
            <button
              type="button"
              onClick={() => setMode('add')}
              className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors ${
                mode === 'add'
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              + {t('roomPage.addPoints')}
            </button>
            <button
              type="button"
              onClick={() => setMode('remove')}
              className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors ${
                mode === 'remove'
                  ? 'bg-red-500/20 text-red-300 border border-red-500/40'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              &minus; {t('roomPage.removePoints')}
            </button>
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase mb-2">
              {t('roomPage.amount')}
            </label>
            <input
              type="number"
              min={0}
              max={9999}
              step={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-muted/10 border border-muted/20 text-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
              placeholder="0"
              autoFocus
            />
            <p className="mt-2 text-xs text-muted-foreground">{t('roomPage.startingPointsHint')}</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase mb-2">
              {t('roomPage.startingPointsNote')}
            </label>
            <input
              type="text"
              maxLength={255}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-muted/10 border border-muted/20 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
              placeholder={t('roomPage.startingPointsNotePlaceholder')}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl font-semibold bg-muted/20 border border-muted/20 text-foreground hover:bg-muted/30 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-3 rounded-xl font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isLoading ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SetStartingPointsModal
